import { analytics } from 'lib/analytics';
import { makeAutoObservable } from 'mobx';
import { waitForElementWithDefinition } from 'utils/waitForElement';

export type UserData = {
  token: string;
  refreshToken: string;
  expiration: number;
  refreshExpiration: number;
  endDate: string;
  endDateRefresh: string;
  dataUser: {
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
    suspended: string;
    id: number;
    phoneHome: string;
    isProducer: boolean;
    billing: {
      label: null | string;
      creditCardName: null | string;
      cpf: null | string;
      birthdate: null | string;
      phoneNum: null | string;
      addressZipCode: null | string;
      addressStreet: null | string;
      addressNum: null | string;
      addressStreet2: null | string;
      addressNeighborhood: null | string;
      addressCity: null | string;
      addressState: null | string;
    };
  };
};

export enum ModalType {
  SIGNIN,
  FORGOTPSW,
  SIGNUP
}

export type AuthHydration = {
  userData: UserData | null;
};

export function saveSession(userData: UserData, shouldKeepSession: boolean) {
  if (shouldKeepSession) {
    localStorage.setItem('userData', JSON.stringify(userData));
    return;
  }
  sessionStorage.setItem('userData', JSON.stringify(userData));
}

function removeUserData() {
  localStorage.removeItem('userData');
  sessionStorage.removeItem('userData');
}

async function getUserData(redisSessionCookie?: string) {
  if (!redisSessionCookie) {
    removeUserData();
    return null;
  }

  const symplaAuthElement = (await waitForElementWithDefinition(
    '#symplaAuth'
  )) as unknown as { getRedisSession: (cookie: string) => unknown };

  if (!symplaAuthElement) {
    return null;
  }

  try {
    const getUserSession =
      symplaAuthElement.getRedisSession?.(redisSessionCookie);
    return getUserSession;
  } catch (error) {
    return null;
  }
}

export class AuthStore {
  isRecoveringSession: boolean;

  isHydrated: boolean;

  modalType: ModalType;

  isModalOpen: boolean;

  isModalAuthenticatedVisible: boolean;

  userData: UserData | null;

  isAuthenticated = false;

  constructor() {
    this.isRecoveringSession = true;
    this.isHydrated = false;
    this.modalType = ModalType.SIGNIN;
    this.isModalOpen = false;
    this.isModalAuthenticatedVisible = false;
    this.userData = null;

    makeAutoObservable(this);
  }

  setModalType = (modalType: ModalType) => {
    this.modalType = modalType;
  };

  setModalOpen = (isModalOpen: boolean) => {
    this.isModalOpen = isModalOpen;
  };

  getIsModalOpen = () => this.isModalOpen;

  onCloseModal = () => {
    this.setModalOpen(false);
  };

  getIsModalAuthenticatedVisible = () => this.isModalAuthenticatedVisible;

  setModalAuthenticatedVisible = (isOpen: boolean) => {
    this.isModalAuthenticatedVisible = isOpen;
  };

  openForgotPswModal = () => {
    this.setModalType(ModalType.FORGOTPSW);
    this.setModalOpen(true);
  };

  openSignInModal = () => {
    this.setModalType(ModalType.SIGNIN);
    this.setModalOpen(true);
  };

  openSignUpModal = () => {
    this.setModalType(ModalType.SIGNUP);
    this.setModalOpen(true);
  };

  updateUserData = async (userData: UserData, shouldKeepSession: boolean) => {
    this.userData = userData;
    this.isAuthenticated = true;
    saveSession(userData, shouldKeepSession);

    analytics.identifyUser?.({
      id: userData?.dataUser?.id,
      name: userData?.dataUser?.fullName,
      email: userData?.dataUser?.email,
      phone: userData?.dataUser?.phoneHome,
      birthdate: userData?.dataUser?.billing?.birthdate,
      city: userData?.dataUser?.billing?.addressCity,
      state: userData?.dataUser?.billing?.addressState
    });
  };

  // get isAuthenticated() {
  //   return this.userData !== null;
  // }

  setUserData = (userData: UserData | null) => {
    this.userData = userData;
    this.isAuthenticated = userData !== null;
  };

  getSession = async (redisSessionCookie?: string) => {
    const userData = (await getUserData(redisSessionCookie)) as UserData;
    if (
      userData &&
      userData.dataUser &&
      Object.keys(userData.dataUser)?.length > 0
    ) {
      this.isAuthenticated = true;
      this.setUserData(userData);
    }
  };

  setIsRecoveringSession = (isRecoveringSession: boolean) => {
    this.isRecoveringSession = isRecoveringSession;
  };

  logOut = () => {
    removeUserData();

    this.userData = null;
    this.isAuthenticated = false;
    this.isModalOpen = false;
    this.isModalAuthenticatedVisible = false;

    this.setUserData(null);
  };

  hydrate = (data: AuthHydration) => {
    if (this.isHydrated) return;

    this.userData = data?.userData || null;
    this.isAuthenticated = !!this.userData;
    this.isRecoveringSession = false;

    this.isHydrated = true;
  };
}

const authStore = new AuthStore();
export default authStore;
